import { captureRegion }        from '@tokamap/core';
import { lngLatToMercator }     from '@tokamap/core';
import { normalizeBbox }        from '@tokamap/core';
import type { CaptureImage }    from '@tokamap/core';
import type { CapturePlan }     from '@tokamap/core';
import type { MapLibreAdapter } from '@tokamap/core';
import type { MercatorBbox }    from '@tokamap/core';
import type { TileLayerName }   from '@tokamap/core';

/**
 * **Le raccord entre une zone entourée sur la carte et le parcours d'import de plan**
 * (`services/map/docs/PLAN_ANALYSE_ZONE_CARTE.md`, lot 4).
 *
 * L'idée du parcours : au lieu d'importer un fichier, l'opérateur pose une image sur la carte,
 * entoure la partie à analyser, et c'est la CAPTURE de cette zone qui part comme plan. Le serveur
 * n'a rien à apprendre — une capture PNG est un import `map_image` comme un autre, avec ses
 * écrans de vérification et sa génération.
 *
 * Ce module ne fait aucun appel réseau : il fabrique le fichier à poster. L'enchaînement des
 * requêtes vit dans l'écran, comme pour le reste du parcours (voir `planShapes.ts`,
 * `planBackground.ts` — mêmes règles).
 *
 * ## ⚠️ Le descripteur de placement ne se calcule PAS ici
 *
 * L'intention du parcours est de supprimer l'étape de placement manuel : la zone étant déjà en
 * coordonnées de la carte, le descripteur devrait se déduire. Il ne peut simplement pas se déduire
 * DEPUIS LE FRONT, et une première version de ce module s'y est trompée.
 *
 * La raison : `PlacementProjector` (service `map`) compose le placement avec la `frozenTransform`
 * que `POST /plan/preview` a gelée — et cette transform cale la boîte pixel sur la **bbox de la
 * scène**, c'est-à-dire l'étendue des seules formes RETENUES (`SceneTransform::fromScene`, voir
 * `MapDxfPreviewService::preview` : « l'aperçu de placement, c'est le résultat »). Cette boîte est
 * un sous-rectangle quelconque de la capture, et le front ne sait pas lequel : l'aperçu ne rend son
 * enveloppe qu'en pixels de lui-même. Calculer l'échelle sur la largeur de la capture, ou sur celle
 * de l'aperçu d'analyse, revient à confondre deux repères — les formes sortiraient à l'échelle et à
 * la position d'un cadrage qu'on n'a pas.
 *
 * Le calcul appartient donc au SERVEUR, qui tient les deux bouts : il vient de fabriquer la
 * transform, et il connaît le repère du document analysé. Voir
 * `services/map/docs/PLAN_ANALYSE_ZONE_CARTE.md` §4 — c'est l'option « dériver le placement côté
 * serveur », qui n'est plus facultative.
 */

/**
 * Nom du fichier de capture.
 *
 * ⚠️ L'extension `.png` n'est pas décorative : `createPlanImport` déduit le TYPE d'import de
 * l'extension du fichier (voir `planImportTypeReference`), et c'est elle qui fait de cette capture
 * un `map_image`. Un nom sans extension partirait en `map_dxf` et le serveur répondrait un 422
 * parlant d'un en-tête DXF, sur une image.
 */
export function captureFileName(at: Date = new Date()): string {
	return `zone-${at.toISOString().replace(/[:.]/g, '-')}.png`;
}

/**
 * Rend la zone en image et l'emballe en fichier prêt à poster, avec le plan de capture qu'il
 * faudra pour en déduire le placement.
 *
 * Les images viennent de l'adaptateur (`imagePlacementsForCapture`) et non d'un calcul local : lui
 * seul sait où elles sont réellement posées, mode "contain" et anti-débordement compris.
 */
export async function captureImportSource(params: {
	adapter: MapLibreAdapter;
	bbox: MercatorBbox;
	basemap: TileLayerName;
	/** Restreint aux images nommées ; toutes celles de la carte sans lui. */
	shapeIds?: string[];
	targetPixels?: number;
}): Promise<{ file: File; plan: CapturePlan }> {
	const { blob, ...plan } = await captureRegion({
		bbox: params.bbox,
		basemap: params.basemap,
		images: params.adapter.imagePlacementsForCapture(params.shapeIds),
		...(params.targetPixels === undefined ? {} : { targetPixels: params.targetPixels }),
	});

	return { file: new File([blob], captureFileName(), { type: 'image/png' }), plan };
}

/**
 * Les images retenues qui posent au moins un pixel DANS la zone.
 *
 * `captureImportSource` prend volontairement TOUTES les images de la carte : une image hors cadre
 * ne se voit simplement pas dans la capture, et calculer une intersection pour raccourcir une liste
 * ne changerait rien à l'image produite. Elle en change une autre, en revanche : savoir s'il y a
 * quelque chose À ANALYSER. Une zone sans image ne porte que le fond de carte, et un fond de carte
 * ne se détecte pas — le bâti d'OSM ne diffère du sol que de 8 à 11 niveaux par canal quand le
 * quantizer du serveur fusionne tout ce qui est sous 40 (mesuré sur une capture réelle : deux
 * couleurs, dont le fond, donc un seul pseudo-calque là où il en faut deux). L'analyse répondrait
 * « 0 calque(s) », toujours.
 *
 * Le recouvrement est testé sur la BOÎTE du quad, pas sur le quad lui-même : une image pivotée
 * dont seul le coin frôle la zone compterait comme dedans. C'est le sens qu'on veut ici — le doute
 * profite à l'opérateur, on l'avertit, on ne lui interdit rien.
 */
export function imagesInRegion(images: CaptureImage[], bbox: MercatorBbox): CaptureImage[] {
	const zone = normalizeBbox(bbox);

	return images.filter(image => {
		const corners = image.quad.map(corner => lngLatToMercator(corner));
		const xs = corners.map(([x]) => x);
		const ys = corners.map(([, y]) => y);

		return Math.min(...xs) <= zone.maxX
			&& Math.max(...xs) >= zone.minX
			&& Math.min(...ys) <= zone.maxY
			&& Math.max(...ys) >= zone.minY;
	});
}

/** Étendue de la zone en mètres Mercator — ce qu'un panneau affiche à l'opérateur pendant qu'il
 * trace. Ce ne sont pas des mètres au sol (facteur 1/cos(latitude)), comme partout où cet éditeur
 * parle d'échelle. */
export function regionSpanMeters(bbox: MercatorBbox): { width: number; height: number } {
	return { width: Math.abs(bbox.maxX - bbox.minX), height: Math.abs(bbox.maxY - bbox.minY) };
}
